import type {
  IAPMAppDataService,
  InFieldUser,
  Measurement,
  MeasurementUpsertType,
} from '@cognite/apm-client';
import { FDMClient } from '@cognite/fdm-client';
import type { CogniteClient, HttpResponse } from '@cognite/sdk';

import {
  getSourceCreatedFields,
  getSourceUpdatedFields,
} from '../shared-utils/sourceable-utils';

import { mapMeasurementToUpsertType } from './cdm-measurement-service-utils';
import type { MeasurementUpsertType as CDMMeasurementUpsertType } from './cdm-types';
import type { IMeasurementService } from './measurement-service-interface';

type APMClientDependencies = {
  measurements: IAPMAppDataService<Measurement>;
  userInstanceSpace: string;
  appDataInstanceSpace: string;
};

export class CdmMeasurementService
  extends FDMClient
  implements IMeasurementService
{
  apmClient: APMClientDependencies;
  constructor(client: CogniteClient, apmClient: APMClientDependencies) {
    super(client);
    this.apmClient = apmClient;
  }

  async createMeasurements(measurements: Measurement[], user: InFieldUser) {
    const upsertedMeasurements: Measurement[] = measurements.map(
      (measurement) => ({
        ...measurement,
        ...getSourceCreatedFields(user),
      })
    );
    return await this.upsertMeasurements(upsertedMeasurements);
  }

  async updateMeasurements(measurements: Measurement[], user: InFieldUser) {
    const upsertedMeasurements: Measurement[] = measurements.map(
      (measurement) => ({
        ...measurement,
        ...getSourceUpdatedFields(user),
      })
    );
    return this.upsertMeasurements(upsertedMeasurements);
  }

  async upsertMeasurements(measurements: Measurement[]) {
    const upsertedMeasurements: CDMMeasurementUpsertType[] = measurements.map(
      (measurement) =>
        mapMeasurementToUpsertType(
          measurement,
          this.apmClient.appDataInstanceSpace
        )
    );
    const measurementsUpserted =
      await this.apmClient.measurements.upsert(upsertedMeasurements);

    return {
      ...measurementsUpserted,
      data: {
        items: measurementsUpserted.data.items.map((measurement) => {
          return {
            ...measurement,
            timeseries: measurement.timeseries?.externalId ?? undefined,
          };
        }),
      },
    } satisfies HttpResponse<{ items: MeasurementUpsertType[] }>;
  }

  async deleteMeasurements(measurementExternalIds: string[]) {
    return this.apmClient.measurements.delete(measurementExternalIds);
  }
}

export const createCdmMeasurementService = (
  ...args: ConstructorParameters<typeof CdmMeasurementService>
): IMeasurementService => new CdmMeasurementService(...args);
